import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CountryFlag } from '@/components/CountryFlag/CountryFlag';
import { Flex } from '@/components/Layout/Flex/Flex';
import { ListBox } from '@/components/ListBox/ListBox';
import { getCountryFromPhoneNumber } from '@/utils/helpers/countryUtils';

import { useMfaSmsListManager } from '../hooks/useMfaSmsListManager';

function PhoneNumberList() {
  const { t } = useTranslation('mfaSmsList');
  const { handleSelectPhoneNumber, enrolledPhoneNumbers } =
    useMfaSmsListManager();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // i18n strings
  const noPhoneNumbersText = t('list.noPhoneNumbers');
  const genericError = t('errors.generic');

  const onSelectPhone = useCallback(
    async (value: string) => {
      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        await handleSelectPhoneNumber(Number(value));
      } catch {
        setError(genericError);
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleSelectPhoneNumber, genericError, isSubmitting],
  );

  // Build one selectable option per enrolled phone number, flag first
  const options = useMemo(
    () =>
      enrolledPhoneNumbers.map((phone, index) => {
        const countryInfo = getCountryFromPhoneNumber(phone.phoneNumber);

        return {
          value: String(index),
          label: phone.phoneNumber,
          icon: countryInfo ? (
            <CountryFlag countryCode={countryInfo.countryCode} size="sm" />
          ) : undefined,
        };
      }),
    [enrolledPhoneNumbers],
  );

  if (enrolledPhoneNumbers.length === 0) {
    return <p className="u-textCenter">{noPhoneNumbersText}</p>;
  }

  return (
    <Flex direction="column" gapY={16}>
      {error && <StatusMessage mode="error">{error}</StatusMessage>}
      <ListBox
        id="enrolled-phone-numbers-list"
        options={options}
        onSelect={onSelectPhone}
        showArrow
        showSeparator
      />
    </Flex>
  );
}

export default PhoneNumberList;
